import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Pencil, Plus, Receipt, Trash2 } from 'lucide-react';
import { FineFormModal } from '../components/forms/FineFormModal';
import { CarSelect } from '../components/forms/EntitySelects';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, EmptyState, PageHeader } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { FilterBar, SearchInput } from '../components/ui/FilterBar';
import { Select } from '../components/ui/Input';
import { ConfirmDialog } from '../components/ui/Modal';
import { useDeleteFine, useFines, useFineSummary, useSaveFine } from '../hooks/useApi';
import { toInt, useUrlFilters } from '../hooks/useUrlFilters';
import { errorMessage } from '../services/api';
import { toast } from '../store';
import type { Fine, FineBreakdown } from '../types';
import { countryName, driverName, EUROPE_COUNTRIES, formatCurrency, formatDate, formatNumber } from '../utils/helpers';
import { locale, t, tx } from '../i18n';

const THIS_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => String(THIS_YEAR - i));
/** Short month names in the current language ("jan", "Jan", "Jan.") */
const months = () => Array.from({ length: 12 }, (_, i) => new Date(2000, i, 1).toLocaleDateString(locale(), { month: 'short' }).replace(/\.$/, ''));

export function Fines() {
  const [filters, setFilters] = useUrlFilters({ year: String(THIS_YEAR), car_id: '', country: '', paid: '', search: '', page: '1' });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Fine | null>(null);
  const year = toInt(filters.year);
  const { data: summary } = useFineSummary(year);

  return (
    <>
      <PageHeader
        title={t('Pokuty')}
        subtitle={t('Kto, kde a za koľko – prehľad nákladov na pokuty')}
        actions={
          <>
            <Select
              aria-label={t('Rok')}
              wrapperClassName="w-36"
              placeholder={t('Všetky roky')}
              options={YEARS.map((y) => ({ value: y, label: y }))}
              value={filters.year}
              onChange={(e) => setFilters({ year: e.target.value })}
            />
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />  {t('Pridať pokutu')}
            </Button>
          </>
        }
      />

      {summary && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Tile label={year ? t('Pokuty spolu {year}', { year }) : t('Pokuty spolu')} value={formatCurrency(summary.total)} note={t('{count} pokút', { count: formatNumber(summary.count) })} />
            <Tile label={t('Zaplatila firma')} value={formatCurrency(summary.company_total)} />
            <Tile label={t('Zaplatil vodič')} value={formatCurrency(summary.driver_total)} />
            <Tile
              label={t('Nezaplatené')}
              value={formatCurrency(summary.unpaid_total)}
              note={summary.unpaid_count ? t('{unpaidCount} pokút čaká na úhradu', { unpaidCount: formatNumber(summary.unpaid_count) }) : t('všetko uhradené')}
              alert={summary.unpaid_count > 0}
            />
          </div>

          {summary.count > 0 && (
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              {year && (
                <Card className="lg:col-span-2">
                  <CardHeader title={t('Pokuty podľa mesiacov {year}', { year })} subtitle={t('Suma v € podľa dátumu priestupku')} />
                  <MonthColumns year={year} rows={summary.by_month} />
                </Card>
              )}
              <BarList title={t('Podľa krajiny')} rows={summary.by_country} label={(r) => `${r.label} – ${countryName(r.key)}`} />
              <BarList title={t('Podľa osoby')} rows={summary.by_person} />
              <BarList title={t('Podľa vozidla')} rows={summary.by_car} />
            </div>
          )}
        </>
      )}

      <FineList filters={filters} setFilters={setFilters} onEdit={setEditing} />
      <FineFormModal isOpen={creating} onClose={() => setCreating(false)} />
      <FineFormModal isOpen={!!editing} fine={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function Tile({ label, value, note, alert = false }: { label: string; value: string; note?: string; alert?: boolean }) {
  return (
    <Card padding="sm">
      <p className="text-sm text-secondary-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-secondary-900">{value}</p>
      {note && (
        <p className={alert ? 'mt-0.5 text-xs font-medium text-red-700' : 'mt-0.5 text-xs text-secondary-500'}>
          {alert && '⚠ '}
          {note}
        </p>
      )}
    </Card>
  );
}

/** Single series of magnitudes: one hue, value text in ink, a hover title on every bar. */
function BarList({ title, rows, label = (r) => r.label }: { title: string; rows: FineBreakdown[]; label?: (r: FineBreakdown) => string }) {
  const TOP = 8;
  const shown = rows.slice(0, TOP);
  const rest = rows.slice(TOP);
  if (rest.length) {
    shown.push({ key: 'other', label: t('Ostatné ({rest})', { rest: rest.length }), count: rest.reduce((s, r) => s + r.count, 0), amount: rest.reduce((s, r) => s + r.amount, 0) });
  }
  const max = Math.max(...shown.map((r) => r.amount), 1);
  return (
    <Card>
      <CardHeader title={title} />
      <ul className="space-y-2.5">
        {shown.map((r) => (
          <li key={r.key} title={`${label(r)}: ${formatCurrency(r.amount)} (${r.count}×)`}>
            <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
              <span className="truncate text-secondary-700">{label(r)}</span>
              <span className="shrink-0 tabular-nums text-secondary-900">
                {formatCurrency(r.amount)} <span className="text-xs text-secondary-500">· {r.count}×</span>
              </span>
            </div>
            <div className="h-2 rounded-full bg-primary-100">
              <div className="h-2 rounded-full bg-primary-600" style={{ width: `${Math.max((r.amount / max) * 100, 1.5)}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function MonthColumns({ year, rows }: { year: number; rows: FineBreakdown[] }) {
  const MONTHS = months();
  const byMonth = new Map(rows.map((r) => [r.key, r]));
  const values = MONTHS.map((_, i) => byMonth.get(`${year}-${String(i + 1).padStart(2, '0')}`));
  const max = Math.max(...values.map((v) => v?.amount ?? 0), 1);
  const peak = values.findIndex((v) => v?.amount === max);
  return (
    <div>
      <div className="flex h-40 items-end gap-1.5 border-b border-secondary-300 sm:gap-3">
        {values.map((v, i) => (
          <div
            key={MONTHS[i]}
            className="relative flex h-full flex-1 items-end"
            title={`${MONTHS[i]} ${year}: ${v ? `${formatCurrency(v.amount)} (${v.count}×)` : t('bez pokút')}`}
          >
            {i === peak && v && <span className="absolute -top-0.5 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs text-secondary-700">{formatCurrency(v.amount)}</span>}
            <div
              className="w-full rounded-t bg-primary-600 transition-opacity hover:opacity-80"
              style={{ height: v ? `${Math.max((v.amount / max) * 85, 2)}%` : 0 }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1 flex gap-1.5 text-center text-xs text-secondary-500 sm:gap-3">
        {MONTHS.map((m) => (
          <span key={m} className="flex-1">
            {m}
          </span>
        ))}
      </div>
    </div>
  );
}

type Filters = { year: string; car_id: string; country: string; paid: string; search: string; page: string };

function FineList({ filters, setFilters, onEdit }: { filters: Filters; setFilters: (p: Partial<Filters>) => void; onEdit: (f: Fine) => void }) {
  const page = toInt(filters.page, 1)!;
  const { data, isLoading, isFetching } = useFines({
    year: toInt(filters.year),
    car_id: toInt(filters.car_id),
    country: filters.country,
    paid: filters.paid === '' ? undefined : filters.paid === 'true',
    search: filters.search,
    page,
    page_size: 50,
  });
  const save = useSaveFine();
  const remove = useDeleteFine();
  const [deleting, setDeleting] = useState<Fine | null>(null);

  const markPaid = async (fine: Fine) => {
    try {
      await save.mutateAsync({ id: fine.id, data: { paid: true, paid_by: fine.paid_by ?? 'company' } });
      toast.success(t('Pokuta je označená ako zaplatená'));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const columns: Column<Fine>[] = [
    { key: 'date', header: t('Dátum'), className: 'whitespace-nowrap', render: (f) => formatDate(f.fine_date) },
    {
      key: 'country',
      header: t('Krajina'),
      className: 'whitespace-nowrap',
      render: (f) => (
        <span title={countryName(f.country)}>
          <Badge variant="primary">{f.country}</Badge>
        </span>
      ),
    },
    {
      key: 'car',
      header: t('Vozidlo'),
      render: (f) => (
        <Link to={`/cars/${f.car_id}`} className="whitespace-nowrap font-mono font-medium text-primary-700 hover:underline">
          {f.car?.license_plate}
        </Link>
      ),
    },
    { key: 'person', header: t('Kto'), render: (f) => (f.driver ? driverName(f.driver) : f.person_name || <span className="text-secondary-400">{t('neurčený')}</span>) },
    {
      key: 'reason',
      header: t('Priestupok'),
      render: (f) => (
        <div className="max-w-xs text-sm">
          <p className="truncate">{f.reason || '–'}</p>
          {f.reference && <p className="truncate text-xs text-secondary-500">{t('č. {reference}', { reference: f.reference })}</p>}
        </div>
      ),
    },
    { key: 'amount', header: t('Suma'), className: 'text-right tabular-nums whitespace-nowrap font-medium', headerClassName: 'text-right', render: (f) => formatCurrency(f.amount) },
    {
      key: 'paid',
      header: t('Úhrada'),
      className: 'whitespace-nowrap',
      render: (f) =>
        f.paid ? (
          <Badge variant="success" dot title={f.paid_date ? t('Zaplatené {paidDate}', { paidDate: formatDate(f.paid_date) }) : undefined}>
            {t('Zaplatené')} · {f.paid_by === 'driver' ? t('vodič') : t('firma')}
          </Badge>
        ) : (
          <Badge variant="danger" dot>
            {t('Nezaplatené')}
          </Badge>
        ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Akcie')}</span>,
      className: 'text-right whitespace-nowrap',
      render: (f) => (
        <>
          {!f.paid && (
            <button className="icon-btn" onClick={() => markPaid(f)} title={t('Označiť ako zaplatenú (firma)')} aria-label={t('Označiť ako zaplatenú')}>
              <Check className="h-4 w-4" />
            </button>
          )}
          <button className="icon-btn" onClick={() => onEdit(f)} title={t('Upraviť')} aria-label={t('Upraviť')}>
            <Pencil className="h-4 w-4" />
          </button>
          <button className="icon-btn-danger" onClick={() => setDeleting(f)} title={t('Zmazať')} aria-label={t('Zmazať')}>
            <Trash2 className="h-4 w-4" />
          </button>
        </>
      ),
    },
  ];

  return (
    <Card padding="none" className="mt-6">
      <FilterBar>
        <SearchInput value={filters.search} onChange={(search) => setFilters({ search })} placeholder={t('Meno, priestupok, číslo…')} />
        <CarSelect label={t('Vozidlo')} wrapperClassName="sm:w-56" placeholder={t('Všetky vozidlá')} value={filters.car_id} onChange={(e) => setFilters({ car_id: e.target.value })} />
        <Select
          label={t('Krajina')}
          wrapperClassName="sm:w-44"
          placeholder={t('Všetky krajiny')}
          options={Object.entries(EUROPE_COUNTRIES).map(([code, name]) => ({ value: code, label: `${code} – ${name}` }))}
          value={filters.country}
          onChange={(e) => setFilters({ country: e.target.value })}
        />
        <Select
          label={t('Úhrada')}
          wrapperClassName="sm:w-40"
          placeholder={t('Všetky')}
          options={[
            { value: 'false', label: t('Nezaplatené') },
            { value: 'true', label: t('Zaplatené') },
          ]}
          value={filters.paid}
          onChange={(e) => setFilters({ paid: e.target.value })}
        />
      </FilterBar>
      {data && (
        <p className="border-b border-secondary-100 px-4 py-2 text-sm text-secondary-600">
          {tx('{count} pokút · spolu {sum}', { count: formatNumber(data.total), sum: <strong>{formatCurrency(data.total_amount)}</strong> })}
        </p>
      )}
      <DataTable
        data={data?.fines}
        columns={columns}
        rowKey={(f) => f.id}
        isLoading={isLoading}
        isFetching={isFetching}
        emptyMessage={<EmptyState icon={<Receipt className="h-10 w-10" />} title={t('Žiadne pokuty nezodpovedajú filtru')} />}
        pagination={data && { page, pageSize: 50, total: data.total, onPageChange: (p) => setFilters({ page: String(p) }) }}
      />
      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        loading={remove.isPending}
        title={t('Zmazať pokutu')}
        confirmText={t('Zmazať')}
        message={deleting && t('Zmazať pokutu {amount} ({country}, {fineDate})?', { amount: formatCurrency(deleting.amount), country: deleting.country, fineDate: formatDate(deleting.fine_date) })}
        onConfirm={async () => {
          try {
            await remove.mutateAsync(deleting!.id);
            toast.success(t('Pokuta bola zmazaná'));
          } catch (err) {
            toast.error(errorMessage(err));
          }
          setDeleting(null);
        }}
      />
    </Card>
  );
}
